import { useEffect, useMemo, useState } from 'react';
import { otApi } from '../api';
import { formatMoney, formatDate } from '../lib/format';

type Quote = {
  id: string;
  reference: string;
  title: string;
  status: string;
  customer_name: string | null;
  total_pence: number;
  valid_until: string | null;
  created_at: string;
};

export default function QuotesPage() {
  const [quotes, setQuotes] = useState<Quote[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await otApi<Quote[] | { quotes: Quote[] }>('/quotes');
        if (cancelled) return;
        const list = Array.isArray(data) ? data : (data as { quotes: Quote[] }).quotes || [];
        setQuotes(list);
      } catch (e) { if (!cancelled) setError((e as Error).message); }
    })();
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    if (!quotes) return [];
    return quotes.filter(q2 => {
      if (statusFilter !== 'all' && q2.status !== statusFilter) return false;
      if (q) {
        const hay = [q2.reference, q2.title, q2.customer_name].filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(q.toLowerCase())) return false;
      }
      return true;
    });
  }, [quotes, q, statusFilter]);

  return (
    <div className="p-4 sm:p-6 lg:p-8 w-full">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl sm:text-2xl font-semibold">Quotes</h1>
        <button className="bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold px-4 py-2 rounded">+ New Quote</button>
      </div>

      <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mb-4">
        <input placeholder="Search reference / title / customer" value={q} onChange={e => setQ(e.target.value)}
               className="flex-1 bg-ink-bg border border-ink-border text-ink-text rounded px-3 py-2 text-sm focus:outline-none focus:border-brand-600" />
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
                className="bg-ink-bg border border-ink-border text-ink-text rounded px-3 py-2 text-sm focus:outline-none focus:border-brand-600">
          <option value="all">All statuses</option>
          <option value="draft">Draft</option>
          <option value="sent">Sent</option>
          <option value="accepted">Accepted</option>
          <option value="declined">Declined</option>
          <option value="expired">Expired</option>
        </select>
      </div>

      {error && <div className="bg-red-950/50 border border-red-900 text-red-300 rounded p-3 text-sm mb-4">Error: {error}</div>}
      {quotes === null && !error && <div className="text-ink-muted text-sm">Loading quotes…</div>}
      {quotes && filtered.length === 0 && (
        <div className="bg-ink-card border border-ink-border rounded p-8 text-center text-ink-muted text-sm">
          {quotes.length === 0 ? 'No quotes yet.' : 'No quotes match your filter.'}
        </div>
      )}

      {filtered.length > 0 && (
        <div className="bg-ink-card border border-ink-border rounded overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-ink-bg border-b border-ink-border text-xs uppercase text-ink-muted">
              <tr>
                <th className="text-left px-4 py-2 font-semibold">Ref</th>
                <th className="text-left px-4 py-2 font-semibold">Title</th>
                <th className="text-left px-4 py-2 font-semibold">Customer</th>
                <th className="text-left px-4 py-2 font-semibold">Status</th>
                <th className="text-left px-4 py-2 font-semibold">Valid until</th>
                <th className="text-right px-4 py-2 font-semibold">Total</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(q2 => (
                <tr key={q2.id} className="border-b border-ink-border last:border-0 hover:bg-ink-hover/30">
                  <td className="px-4 py-2 font-mono text-xs">{q2.reference}</td>
                  <td className="px-4 py-2 font-medium">{q2.title || '(untitled)'}</td>
                  <td className="px-4 py-2 text-ink-muted">{q2.customer_name || '—'}</td>
                  <td className="px-4 py-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      q2.status === 'accepted' ? 'bg-green-900/40 text-green-300' :
                      q2.status === 'sent'     ? 'bg-blue-900/40 text-blue-300' :
                      q2.status === 'declined' ? 'bg-red-900/40 text-red-300' :
                      'bg-ink-border text-ink-text'
                    }`}>{q2.status}</span>
                  </td>
                  <td className="px-4 py-2 text-ink-muted">{formatDate(q2.valid_until)}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{formatMoney(q2.total_pence || 0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
